import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { title: "", body: "", priority: "medium", is_active: true };

export default function AdminAnnouncements() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/announcements").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upd = (k) => (v) => {
    const val = v?.target ? v.target.value : v;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...EMPTY, ...it }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/announcements/${editing.id}`, form);
      else await api.post("/announcements", form);
      toast.success(editing ? "Annonce mise à jour" : "Annonce ajoutée");
      setOpen(false); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette annonce ?")) return;
    await api.delete(`/announcements/${id}`); toast.success("Supprimée"); load();
  };

  return (
    <div data-testid="admin-announcements">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-gold-dark text-xs tracking-[0.3em] uppercase">Communication</div>
          <h1 className="font-serif text-3xl sm:text-4xl text-deepblue-900 mt-1">Annonces</h1>
        </div>
        <Button onClick={openCreate} className="bg-gold hover:bg-gold-hover text-white rounded-full" data-testid="add-announcement-btn">
          <Plus className="w-4 h-4 mr-1" /> Ajouter
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Titre</TableHead>
              <TableHead>Priorité</TableHead>
              <TableHead>Active</TableHead>
              <TableHead>Créée</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Aucune annonce</TableCell></TableRow>}
            {items.map((a) => (
              <TableRow key={a.id}>
                <TableCell className="font-medium text-deepblue-900">{a.title}</TableCell>
                <TableCell><span className={`text-xs px-2 py-1 rounded ${a.priority === "high" ? "bg-red-100 text-red-700" : a.priority === "medium" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{a.priority}</span></TableCell>
                <TableCell>{a.is_active ? "✓" : "—"}</TableCell>
                <TableCell>{new Date(a.created_at).toLocaleDateString("fr-FR")}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(a)}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(a.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Modifier" : "Ajouter"} une annonce</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Titre *</Label><Input value={form.title} onChange={upd("title")} data-testid="ann-title" /></div>
            <div><Label>Texte *</Label><Textarea rows={4} value={form.body} onChange={upd("body")} data-testid="ann-body" /></div>
            <div>
              <Label>Priorité</Label>
              <Select value={form.priority} onValueChange={upd("priority")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Basse</SelectItem>
                  <SelectItem value="medium">Moyenne</SelectItem>
                  <SelectItem value="high">Haute</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <span className="text-sm">Publiée sur le site</span>
              <Switch checked={form.is_active} onCheckedChange={upd("is_active")} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} className="bg-gold hover:bg-gold-hover text-white" data-testid="save-ann-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
